/**
 * Визуальный конструктор расписания (генерирует cron-выражение в UTC).
 *
 * Пресеты:
 *  - daily      — каждый день в HH:MM
 *  - weekdays   — Пн–Пт в HH:MM
 *  - weekends   — Сб–Вс в HH:MM
 *  - weekly     — выбранные дни недели в HH:MM
 *  - monthly    — N-го числа месяца в HH:MM
 *  - hourly     — каждый час в MM-ю минуту
 *  - interval   — каждые N минут
 *  - custom     — произвольное cron-выражение (ввод вручную)
 *
 * value — стандартное 5-польное cron-выражение (минута час день месяц день_недели),
 * полностью совместимое с тем, что было до конструктора.
 */
import { useEffect, useMemo, useState } from 'react';
import { Input, InputNumber, Select, Space, TimePicker, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

type PresetKey =
  | 'daily'
  | 'weekdays'
  | 'weekends'
  | 'weekly'
  | 'monthly'
  | 'hourly'
  | 'interval'
  | 'custom';

interface Props {
  value?: string;
  onChange?: (cron: string) => void;
}

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Пн' },
  { value: 2, label: 'Вт' },
  { value: 3, label: 'Ср' },
  { value: 4, label: 'Чт' },
  { value: 5, label: 'Пт' },
  { value: 6, label: 'Сб' },
  { value: 0, label: 'Вс' },
];

interface ParsedState {
  preset: PresetKey;
  time: Dayjs;
  weekDays: number[];
  dayOfMonth: number;
  hourlyMinute: number;
  intervalMinutes: number;
  raw: string;
}

const DEFAULT_STATE: ParsedState = {
  preset: 'daily',
  time: dayjs().hour(9).minute(0),
  weekDays: [1],
  dayOfMonth: 1,
  hourlyMinute: 0,
  intervalMinutes: 15,
  raw: '0 9 * * *',
};

// Время пользователю показывается в МСК (UTC+3); cron хранится в UTC.
const TZ_OFFSET_HOURS = 3;

function shiftDow(d: number, shift: number): number {
  return (((d + shift) % 7) + 7) % 7;
}

function fmtDows(dows: number[]): string {
  return Array.from(new Set(dows))
    .sort((a, b) => a - b)
    .join(',');
}

function setsEqual(a: number[], b: number[]): boolean {
  const sa = new Set(a);
  if (sa.size !== b.length) return false;
  for (const x of b) if (!sa.has(x)) return false;
  return true;
}

// MSK → UTC: возвращает UTC-час и сдвиг даты (-1, если 0:00–2:59 МСК ушло на предыдущие сутки UTC).
function mskToUtc(time: Dayjs): { utcM: number; utcH: number; dayShift: -1 | 0 } {
  const mskH = time.hour();
  const m = time.minute();
  const raw = mskH - TZ_OFFSET_HOURS;
  if (raw < 0) return { utcM: m, utcH: raw + 24, dayShift: -1 };
  return { utcM: m, utcH: raw, dayShift: 0 };
}

// UTC → MSK: возвращает время МСК и сдвиг (+1, если +3 часа перевалили за полночь — МСК-сутки на день вперёд).
function utcToMsk(utcH: number, utcM: number): { time: Dayjs; dayShift: 0 | 1 } {
  const raw = utcH + TZ_OFFSET_HOURS;
  if (raw >= 24) {
    return { time: dayjs().hour(raw - 24).minute(utcM).second(0), dayShift: 1 };
  }
  return { time: dayjs().hour(raw).minute(utcM).second(0), dayShift: 0 };
}

function parseCron(expr: string): ParsedState {
  if (!expr) return { ...DEFAULT_STATE };
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return { ...DEFAULT_STATE, preset: 'custom', raw: expr };
  // After length === 5 check, all five elements are defined; помогаем
  // TS noUncheckedIndexedAccess через явное приведение через as.
  const [mStr, hStr, dom, mon, dow] = parts as [string, string, string, string, string];
  const num = (s: string) => (/^\d+$/.test(s) ? Number(s) : NaN);

  // каждые N минут — TZ не влияет
  const intervalMatch = /^\*\/(\d+)$/.exec(mStr);
  if (intervalMatch && hStr === '*' && dom === '*' && mon === '*' && dow === '*') {
    return {
      ...DEFAULT_STATE,
      preset: 'interval',
      intervalMinutes: Number(intervalMatch[1]),
    };
  }

  // ежечасно (M * * * *) — TZ не влияет
  if (!Number.isNaN(num(mStr)) && hStr === '*' && dom === '*' && mon === '*' && dow === '*') {
    return { ...DEFAULT_STATE, preset: 'hourly', hourlyMinute: num(mStr) };
  }

  if (Number.isNaN(num(mStr)) || Number.isNaN(num(hStr)) || mon !== '*') {
    return { ...DEFAULT_STATE, preset: 'custom', raw: expr };
  }
  const { time: tm, dayShift } = utcToMsk(num(hStr), num(mStr));

  // ежемесячно
  if (dow === '*' && /^\d+$/.test(dom)) {
    let mskDom = num(dom) + dayShift;
    if (mskDom > 28) mskDom = 1;
    if (mskDom < 1) mskDom = 28;
    return { ...DEFAULT_STATE, preset: 'monthly', time: tm, dayOfMonth: mskDom };
  }

  // дни недели
  if (dom === '*') {
    if (dow === '*') {
      return { ...DEFAULT_STATE, preset: 'daily', time: tm };
    }
    let utcDows: number[] | null = null;
    if (dow === '1-5') utcDows = [1, 2, 3, 4, 5];
    else if (/^[0-6](,[0-6])*$/.test(dow)) utcDows = dow.split(',').map(Number);

    if (utcDows) {
      const mskDows = utcDows.map((d) => shiftDow(d, dayShift));
      if (setsEqual(mskDows, [1, 2, 3, 4, 5])) {
        return { ...DEFAULT_STATE, preset: 'weekdays', time: tm };
      }
      if (setsEqual(mskDows, [0, 6])) {
        return { ...DEFAULT_STATE, preset: 'weekends', time: tm };
      }
      return { ...DEFAULT_STATE, preset: 'weekly', time: tm, weekDays: mskDows };
    }
  }

  return { ...DEFAULT_STATE, preset: 'custom', raw: expr };
}

function buildCron(state: ParsedState): string {
  // Для этих пресетов TZ роли не играет — только минута
  if (state.preset === 'hourly') return `${state.hourlyMinute} * * * *`;
  if (state.preset === 'interval') return `*/${state.intervalMinutes} * * * *`;
  if (state.preset === 'custom') return state.raw || '0 9 * * *';

  const { utcM, utcH, dayShift } = mskToUtc(state.time);

  switch (state.preset) {
    case 'daily':
      return `${utcM} ${utcH} * * *`;
    case 'weekdays': {
      const utcDows = [1, 2, 3, 4, 5].map((d) => shiftDow(d, dayShift));
      return `${utcM} ${utcH} * * ${fmtDows(utcDows)}`;
    }
    case 'weekends': {
      const utcDows = [0, 6].map((d) => shiftDow(d, dayShift));
      return `${utcM} ${utcH} * * ${fmtDows(utcDows)}`;
    }
    case 'weekly': {
      const src = state.weekDays.length > 0 ? state.weekDays : [1];
      const utcDows = src.map((d) => shiftDow(d, dayShift));
      return `${utcM} ${utcH} * * ${fmtDows(utcDows)}`;
    }
    case 'monthly': {
      let dom = state.dayOfMonth + dayShift;
      if (dom < 1) dom = 28;
      if (dom > 28) dom = 1;
      return `${utcM} ${utcH} ${dom} * *`;
    }
  }
  return '0 9 * * *';
}

export function CronScheduleBuilder({ value, onChange }: Props): JSX.Element {
  const [state, setState] = useState<ParsedState>(() => parseCron(value ?? '0 9 * * *'));

  // Если value меняется снаружи (например, при загрузке правила) — пересинхронизируемся.
  useEffect(() => {
    if (value && value !== buildCron(state)) {
      setState(parseCron(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const cron = useMemo(() => buildCron(state), [state]);

  useEffect(() => {
    if (onChange && cron !== value) onChange(cron);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cron]);

  const update = (patch: Partial<ParsedState>) => setState((s) => ({ ...s, ...patch }));

  const presetSelect = (
    <Select
      value={state.preset}
      onChange={(v: PresetKey) => update({ preset: v })}
      style={{ minWidth: 200 }}
      options={[
        { label: 'каждый день', value: 'daily' },
        { label: 'по будням', value: 'weekdays' },
        { label: 'по выходным', value: 'weekends' },
        { label: 'по дням недели', value: 'weekly' },
        { label: 'ежемесячно', value: 'monthly' },
        { label: 'ежечасно', value: 'hourly' },
        { label: 'каждые N минут', value: 'interval' },
        { label: 'произвольное', value: 'custom' },
      ]}
    />
  );

  const timePicker = (
    <TimePicker
      value={state.time}
      format="HH:mm"
      allowClear={false}
      minuteStep={1}
      onChange={(t) => t && update({ time: t })}
    />
  );

  const dayChip = (d: { value: number; label: string }) => {
    const active = state.weekDays.includes(d.value);
    return (
      <button
        key={d.value}
        type="button"
        onClick={() =>
          update({
            weekDays: active
              ? state.weekDays.filter((x) => x !== d.value)
              : [...state.weekDays, d.value],
          })
        }
        style={{
          minWidth: 38,
          height: 30,
          padding: '0 10px',
          borderRadius: 6,
          border: `1px solid ${active ? '#1677ff' : '#d9d9d9'}`,
          background: active ? '#e6f4ff' : '#fff',
          color: active ? '#1677ff' : 'inherit',
          fontWeight: active ? 600 : 400,
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        {d.label}
      </button>
    );
  };

  const renderSentence = () => {
    switch (state.preset) {
      case 'daily':
      case 'weekdays':
      case 'weekends':
        return (
          <>
            {presetSelect}
            <Typography.Text>в</Typography.Text>
            {timePicker}
          </>
        );
      case 'weekly':
        return (
          <>
            {presetSelect}
            <Typography.Text>в</Typography.Text>
            {timePicker}
          </>
        );
      case 'monthly':
        return (
          <>
            {presetSelect}
            <Typography.Text>каждое</Typography.Text>
            <InputNumber
              min={1}
              max={28}
              value={state.dayOfMonth}
              onChange={(v) => update({ dayOfMonth: Number(v) || 1 })}
              style={{ width: 70 }}
            />
            <Typography.Text>число в</Typography.Text>
            {timePicker}
          </>
        );
      case 'hourly':
        return (
          <>
            {presetSelect}
            <Typography.Text>на</Typography.Text>
            <InputNumber
              min={0}
              max={59}
              value={state.hourlyMinute}
              onChange={(v) => update({ hourlyMinute: Math.max(0, Math.min(59, Number(v) || 0)) })}
              style={{ width: 70 }}
            />
            <Typography.Text>-й минуте</Typography.Text>
          </>
        );
      case 'interval':
        return (
          <>
            {presetSelect}
            <InputNumber
              min={1}
              max={59}
              value={state.intervalMinutes}
              onChange={(v) =>
                update({ intervalMinutes: Math.max(1, Math.min(59, Number(v) || 1)) })
              }
              style={{ width: 70 }}
            />
            <Typography.Text>мин</Typography.Text>
          </>
        );
      case 'custom':
        return presetSelect;
    }
  };

  return (
    <div
      style={{
        padding: 12,
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        background: '#fafafa',
      }}
    >
      <Space wrap size={[8, 8]} align="center" style={{ rowGap: 8 }}>
        <Typography.Text strong>Запускать</Typography.Text>
        {renderSentence()}
      </Space>

      {state.preset === 'weekly' && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {WEEKDAYS.map(dayChip)}
        </div>
      )}

      {state.preset === 'custom' && (
        <div style={{ marginTop: 10 }}>
          <Input
            placeholder="минута час день_месяца месяц день_недели (UTC)"
            value={state.raw}
            onChange={(e) => update({ raw: e.target.value })}
          />
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
        Время указывается по Москве (UTC+3). Cron в UTC:{' '}
        <Typography.Text code style={{ fontSize: 12 }}>{cron}</Typography.Text>
      </div>
    </div>
  );
}
